import React, { useState, useEffect } from 'react';
import { hasPermission } from '../utils/permissions';
import { Switch, FormControlLabel } from '@mui/material';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { useUIActions } from '../context/UIActionsContext.jsx';
// @ts-ignore
import { Button as CommonButton, LoadingSpinner, Modal, ConfirmModal, FormInput, FormTextarea, FormSelect } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import {
    PlusCircle, Search, LayoutGrid, List, MoreHorizontal,
    Play, Pause, Trash2, Edit, Loader2, Zap, Mail, Webhook, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const LISTENER_TYPES = [
    { value: 'EMAIL_GRAPH', label: 'Email (Microsoft Graph)', icon: 'Mail' },
    { value: 'WEBHOOK', label: 'Inbound Webhook', icon: 'Webhook' },
    // { value: 'S3_BUCKET', label: 'S3 File Upload', icon: 'FileText' } // Future
];

const EventListenersTab = ({ token, currentUser, isAdmin, onToolbarChange }) => {
    const { notify } = useUIActions();

    // Admin State
    const [showAllTenants, setShowAllTenants] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterTenantId, setFilterTenantId] = useState(currentUser?.roleId === 'r3' ? currentUser.tenantId : 'all');
    const [targetTenantId, setTargetTenantId] = useState(isAdmin ? null : currentUser?.tenantId);
    const [viewLayout, setViewLayout] = useState('list'); // 'grid' | 'list'

    // Modals
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingListener, setEditingListener] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmSubscribe, setConfirmSubscribe] = useState(null);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [localhostDomain, setLocalhostDomain] = useState('');
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Form
    const initialFormState = {
        listener_type: undefined, // undefined prevents Radix Select issues with empty strings
        source_address: '', // Mailbox or Webhook path
        integration_id: undefined, // API Key to use
        target_flow_id: undefined, // Flow to trigger
        is_active: true,
        criteria_json: {} // Simple JSON for now
    };
    const [formData, setFormData] = useState(initialFormState);

    // Fetch Data
    const effectiveTenantId = isAdmin 
        ? (showAllTenants ? filterTenantId : (currentUser?.roleId === 'r3' ? currentUser.tenantId : 'platform'))
        : currentUser?.tenantId;

    // Use a placeholder if tenant is not selected (for admin root view)
    const { data: listeners, isLoading: loadingListeners, refresh, setData: setListeners } = useData(
        effectiveTenantId ? `/integrations/listeners/tenants/${effectiveTenantId}` : null,
        token
    );

    const { data: tenantsList } = useData(isAdmin ? '/tenants' : null, token);
    const tenantsMap = (tenantsList || []).reduce((acc, t) => ({ ...acc, [t.id]: t.name }), {});

    // For selectors
    const { data: flows } = useData(targetTenantId ? `/flows/tenants/${targetTenantId}` : null, token);
    const { data: apiKeys } = useData(targetTenantId ? `/integrations/tenants/${targetTenantId}/api-keys` : null, token); // Selects integration credentials based on the modal's TARGET tenant

    const filteredListeners = (listeners || []).filter(listener => {
        const matchesSearch = listener.source_address.toLowerCase().includes(searchQuery.toLowerCase()) ||
            listener.listener_type.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
    });

    useEffect(() => {
        if (editingListener) {
            setFormData({
                listener_type: editingListener.listener_type,
                source_address: editingListener.source_address,
                integration_id: editingListener.integration_id || undefined,
                target_flow_id: editingListener.target_flow_id,
                is_active: editingListener.is_active,
                criteria_json: editingListener.criteria_json || {}
            });
            if (isAdmin) setTargetTenantId(editingListener.tenant_id);
        } else {
            setFormData(initialFormState);
            if (isAdmin) {
                setTargetTenantId(filterTenantId === 'all' ? null : filterTenantId);
            } else {
                setTargetTenantId(currentUser?.tenantId);
            }
        }
    }, [editingListener, filterTenantId]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    useEffect(() => {
        if (apiKeys && apiKeys.length === 1 && !formData.integration_id) {
            setFormData(prev => ({ ...prev, integration_id: apiKeys[0].id }));
        }
    }, [apiKeys, formData.integration_id]);

    const handleSelectChange = async (name, value) => {
        let updates = { [name]: value };
        
        if (name === 'target_flow_id' && value && formData.listener_type === 'EMAIL_GRAPH') {
            try {
                // Fetch the selected flow to extract its email listener node configuration
                const flowData = await apiFetch(`/flows/${value}`, token);
                if (flowData && flowData.graphJson) {
                    const nodes = typeof flowData.graphJson === 'string' ? JSON.parse(flowData.graphJson).nodes : (flowData.graphJson.nodes || []);
                    const emailNode = nodes.find(n => n.type === 'EVENTLISTENEREMAIL' || n.type === 'eventListenerEmail' || n.type === 'EVENT_LISTENER_EMAIL');
                    const nodeData = emailNode ? (emailNode.data || emailNode.config) : null;
                    if (nodeData) {
                        const { connectionId, monitored_mailbox, subject_filter, body_filter, from_filter, has_attachments } = nodeData;
                        updates.source_address = monitored_mailbox || '';
                        updates.integration_id = (connectionId && connectionId !== 'new') ? connectionId : 'none';
                        
                        // Fully replace criteria to match the selected flow
                        const newCriteria = {};
                        if (subject_filter) newCriteria.subject_contains = subject_filter;
                        if (body_filter) newCriteria.body_contains = body_filter;
                        if (from_filter) newCriteria.from_contains = from_filter;
                        if (has_attachments) newCriteria.has_attachments = true;
                        
                        updates.criteria_json = newCriteria;
                        notify.success("Automatically pulled email configuration from the selected flow.");
                    }
                }
            } catch (e) {
                console.error("Failed to parse flow data for auto-configuration", e);
            }
        }
        
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const criteriaObj = typeof formData.criteria_json === 'string'
        ? (() => { try { return JSON.parse(formData.criteria_json || '{}') } catch { return {} } })()
        : (formData.criteria_json || {});

    const updateCriteria = (key, value) => {
        const newCriteria = { ...criteriaObj };
        if (value === '' || value === false || value === null) {
            delete newCriteria[key];
        } else {
            newCriteria[key] = value;
        }
        setFormData({ ...formData, criteria_json: newCriteria });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isAdmin && !targetTenantId) {
            notify.error("Please select a target tenant first.");
            return;
        }

        const currentCriteriaStr = typeof formData.criteria_json === 'string' 
            ? formData.criteria_json 
            : JSON.stringify(formData.criteria_json || {});

        const isDuplicate = listeners?.some(l => {
            const sameBase = l.tenant_id === targetTenantId &&
                l.listener_type === formData.listener_type &&
                l.source_address === formData.source_address &&
                l.target_flow_id === formData.target_flow_id &&
                (!editingListener || l.id !== editingListener.id);
            
            if (!sameBase) return false;

            const existingCriteriaStr = typeof l.criteria_json === 'string'
                ? l.criteria_json
                : JSON.stringify(l.criteria_json || {});
                
            return existingCriteriaStr === currentCriteriaStr;
        });

        if (isDuplicate) {
            notify.error("A duplicate listener configuration already exists.");
            return;
        }

        const payload = {
            ...formData,
            integration_id: formData.integration_id === 'none' ? null : formData.integration_id,
            // Ensure criteria is a proper object, parse if string
            criteria_json: typeof formData.criteria_json === 'string' ? JSON.parse(formData.criteria_json || '{}') : formData.criteria_json
        };

        const action = editingListener ? 'update' : 'create';
        try {
            if (editingListener) {
                await apiFetch(`/integrations/listeners/tenants/${targetTenantId}/${editingListener.id}`, token, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                });
            } else {
                await apiFetch(`/integrations/listeners/tenants/${targetTenantId}`, token, {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            }
            setIsModalOpen(false);
            notify.success(`Listener ${action}d successfully.`);
            refresh();
        } catch (err) {
            notify.error(`Failed to ${action} listener: ${err.message}`);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        
        // Optimistic UI update
        const previousListeners = listeners;
        setListeners(prev => (prev || []).filter(l => l.id !== confirmDelete.id));
        
        try {
            await apiFetch(`/integrations/listeners/tenants/${confirmDelete.tenant_id}/${confirmDelete.id}`, token, {
                method: 'DELETE'
            });
            notify.success('Listener deleted successfully');
            setConfirmDelete(null);
            refresh(true); // background refresh
        } catch (err) {
            // Revert on failure
            setListeners(previousListeners);
            notify.error(err.message);
        }
    };

    const handleToggleStatus = async (listener) => {
        // Optimistic UI update
        setListeners(prev => (prev || []).map(l => l.id === listener.id ? { ...l, is_active: !listener.is_active } : l));
        
        try {
            await apiFetch(`/integrations/listeners/tenants/${listener.tenant_id}/${listener.id}`, token, {
                method: 'PUT',
                body: JSON.stringify({ is_active: !listener.is_active })
            });
            notify.success(`Listener ${!listener.is_active ? 'activated' : 'deactivated'} successfully.`);
            refresh(true); // background refresh to ensure sync
        } catch (err) {
            // Revert on failure
            setListeners(prev => (prev || []).map(l => l.id === listener.id ? { ...l, is_active: listener.is_active } : l));
            notify.error(`Failed to toggle status: ${err.message}`);
        }
    };

    const handleSubscribeGraph = (listener) => {
        setConfirmSubscribe(listener);
    };

    const executeSubscribeGraph = () => {
        if (!confirmSubscribe) return;
        const listener = confirmSubscribe;
        
        let fullUrl = "";
        
        if (isLocalhost && localhostDomain) {
            let cleanDomain = localhostDomain.trim().replace(/\/+$/, '');
            // For localhost testing via ngrok, enforce a clean https:// scheme
            cleanDomain = cleanDomain.replace(/^(https?:\/\/\s*)+/i, '');
            cleanDomain = `https://${cleanDomain}`;
            
            // Extract the API path dynamically from the env var (e.g., /api/v1)
            let apiPath = '/api/v1';
            try {
                const envUrl = import.meta.env.VITE_API_BASE_URL;
                if (envUrl) {
                    apiPath = new URL(envUrl, window.location.origin).pathname.replace(/\/+$/, '');
                }
            } catch (e) {
                // Ignore URL parsing errors and stick to default fallback
            }
            fullUrl = `${cleanDomain}${apiPath}/integrations/listeners/webhooks/graph/notify`;
        } else {
            const baseApiUrl = (import.meta.env.VITE_API_BASE_URL || window.location.origin).trim().replace(/\/+$/, '');
            fullUrl = `${baseApiUrl}/integrations/listeners/webhooks/graph/notify`;
        }

        setIsSubscribing(true);
        apiFetch(`/integrations/listeners/email/subscribe?tenant_id=${listener.tenant_id}&mailbox=${encodeURIComponent(listener.source_address)}&notification_url=${encodeURIComponent(fullUrl)}&listener_id=${listener.id}`, token, {
            method: 'POST'
        }).then(response => {
            notify.success("Successfully subscribed to Microsoft Graph!");
            
            if (response && response.subscription && response.subscription.id) {
                setListeners(prev => (prev || []).map(l => 
                    l.id === listener.id ? { ...l, subscription_id: response.subscription.id } : l
                ));
            }

            refresh(true); // background refresh
        }).catch(err => {
            let errMsg = err.message || "Unknown error";
            try {
                const jsonStart = errMsg.indexOf('{');
                if (jsonStart !== -1) {
                    const parsed = JSON.parse(errMsg.substring(jsonStart));
                    if (parsed.error?.message) {
                        errMsg = parsed.error.message;
                    } else if (parsed.message) {
                        errMsg = parsed.message;
                    }
                }
            } catch (e) {
                // Keep original error message if parsing fails
            }
            notify.error(`Failed to subscribe: ${errMsg}`);
        }).finally(() => {
            setIsSubscribing(false);
            setConfirmSubscribe(null);
        });
    };

    const getFlowName = (flowId) => {
        if (!flows) return flowId;
        const flow = flows.find(f => f.id === flowId);
        if (!flow) return flowId;
        return flow.publishedName ? `${flow.name} (Published as ${flow.publishedName})` : flow.name;
    };

    const getIntegrationName = (integrationId) => {
        if (!apiKeys || !integrationId) return 'None';
        const key = apiKeys.find(k => k.id === integrationId);
        return key ? `${key.provider} (${key.name || 'Unnamed'})` : 'Unknown';
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'EMAIL_GRAPH': return <Mail className="w-4 h-4 text-blue-500" />;
            case 'WEBHOOK': return <Webhook className="w-4 h-4 text-purple-500" />;
            default: return <Zap className="w-4 h-4 text-amber-500" />;
        }
    };

    const openCreateModal = () => {
        setEditingListener(null);
        setIsModalOpen(true);
    };

    const filterControls = (
        <div className="flex items-center gap-4">
            <div className="relative max-w-md group w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-[#002F5D] dark:text-slate-500 transition-colors" />
                <Input
                    type="text"
                    placeholder="Search listeners..."
                    className="pl-10 bg-white dark:bg-slate-900"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {isAdmin && (
                <div className="flex items-center gap-4">
                    <Select
                        value={filterTenantId}
                        onValueChange={setFilterTenantId}
                        disabled={!showAllTenants}
                    >
                        <SelectTrigger className="w-[180px] h-9 text-sm bg-white dark:bg-slate-900">
                            <SelectValue placeholder="All Tenants" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Tenants</SelectItem>
                            <SelectItem value="platform" className="text-blue-600 font-medium">Platform System</SelectItem>
                            {Array.from(new Map((tenantsList || []).map(t => [t.id, t])).values())
                                .filter(t => t.id !== 'platform')
                                .map(t => (
                                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showAllTenants}
                                onChange={(e) => setShowAllTenants(e.target.checked)}
                                size="small"
                            />
                        }
                        label={<span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">All Tenants</span>}
                        className="mr-0"
                    />
                </div>
            )}

            <div className="flex items-center bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800 p-1 ml-auto">
                <Button
                    variant={viewLayout === 'grid' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`px-2 h-8 ${viewLayout === 'grid' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setViewLayout('grid')}
                >
                    <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button
                    variant={viewLayout === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className={`px-2 h-8 ${viewLayout === 'list' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400 dark:hover:text-slate-200'}`}
                    onClick={() => setViewLayout('list')}
                >
                    <List className="w-4 h-4" />
                </Button>
            </div>

            <CommonButton onClick={openCreateModal} sx={{ height: 32 }}>
                <PlusCircle className="mr-2 h-4 w-4" /> NEW LISTENER
            </CommonButton>
        </div>
    );

    const renderListView = (items) => (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <Table>
                <TableHeader className="bg-gray-50/50 dark:bg-slate-900/50">
                    <TableRow className="border-b border-gray-200 dark:border-slate-800">
                        <TableHead className="text-gray-500 dark:text-slate-400">Type</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Source / Address</TableHead>
                        {isAdmin && showAllTenants && <TableHead className="text-gray-500 dark:text-slate-400">Tenant</TableHead>}
                        <TableHead className="text-gray-500 dark:text-slate-400">Target Flow</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Integration</TableHead>
                        <TableHead className="text-gray-500 dark:text-slate-400">Status</TableHead>
                        <TableHead className="text-right text-gray-500 dark:text-slate-400">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map((listener) => (
                        <TableRow key={listener.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800">
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    {getTypeIcon(listener.listener_type)}
                                    <span className="font-medium text-gray-900 dark:text-slate-100 text-sm">
                                        {LISTENER_TYPES.find(t => t.value === listener.listener_type)?.label || listener.listener_type}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell className="text-gray-600 dark:text-slate-300 text-sm">
                                {listener.source_address}
                            </TableCell>
                            {isAdmin && showAllTenants && (
                                <TableCell className="text-gray-500 dark:text-slate-400">
                                    {listener.tenant_id ? (tenantsMap[listener.tenant_id] || listener.tenant_id) : <span className="text-blue-500">System</span>}
                                </TableCell>
                            )}
                            <TableCell className="text-gray-500 dark:text-slate-400 text-sm">
                                <span className="inline-flex items-center px-2 py-0.5 rounded font-medium bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 border border-purple-100 dark:border-purple-800/30 truncate max-w-[200px]" title={getFlowName(listener.target_flow_id)}>
                                    <DynamicIcon name="Workflow" className="w-3 h-3 mr-1" />
                                    <span className="truncate">{getFlowName(listener.target_flow_id)}</span>
                                </span>
                            </TableCell>
                            <TableCell className="text-gray-500 dark:text-slate-400 text-xs">
                                {getIntegrationName(listener.integration_id)}
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={listener.is_active}
                                        onChange={() => handleToggleStatus(listener)}
                                        size="small"
                                    />
                                    {listener.listener_type === 'EMAIL_GRAPH' && (
                                        <Button 
                                            variant={listener.subscription_id ? "outline" : "default"} 
                                            size="sm" 
                                            onClick={() => handleSubscribeGraph(listener)}
                                            className="text-xs h-7 ml-2"
                                        >
                                            {listener.subscription_id ? 'Update Webhook' : 'Subscribe'}
                                        </Button>
                                    )}
                                </div>
                            </TableCell>
                            <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                    {hasPermission(currentUser, 'content:manage') && (
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        {hasPermission(currentUser, 'settings:manage_integrations') || hasPermission(currentUser, 'content:manage') ? (
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => { setEditingListener(listener); setIsModalOpen(true); }}>
                                                    <Edit className="w-4 h-4 mr-2" /> Edit
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setConfirmDelete(listener)} className="text-red-600">
                                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        ) : null}
                                    </DropdownMenu>
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );

    const renderCardView = (items) => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map((listener) => (
                <div key={listener.id} className="group bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-lg transition-all duration-300 relative overflow-hidden flex flex-col h-full">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />

                    <div className="flex justify-between items-start mb-4">
                        <div className={`flex items-center justify-center p-2.5 rounded-xl transition-colors duration-300 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400`}>
                            {getTypeIcon(listener.listener_type)}
                        </div>
                        <div className="flex gap-1">
                            {hasPermission(currentUser, 'settings:manage_integrations') || hasPermission(currentUser, 'content:manage') ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                            <MoreHorizontal className="w-4 h-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => { setEditingListener(listener); setIsModalOpen(true); }}>
                                            <Edit className="w-4 h-4 mr-2" /> Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setConfirmDelete(listener)} className="text-red-600">
                                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : null}
                        </div>
                    </div>

                    <div className="mb-4 flex-grow">
                        <h3 className="font-bold text-gray-900 dark:text-slate-100 truncate text-sm mb-1">{listener.source_address}</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{LISTENER_TYPES.find(t => t.value === listener.listener_type)?.label}</p>

                        <div className="space-y-2 text-xs">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-500">Flow:</span>
                                <span className="truncate text-gray-700 dark:text-gray-300 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{getFlowName(listener.target_flow_id)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-slate-500">
                                <span className="font-medium">Integration:</span> <span className="truncate">{getIntegrationName(listener.integration_id)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-gray-50 dark:border-slate-800 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                            <Switch checked={listener.is_active} onChange={() => handleToggleStatus(listener)} size="small" />
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400 capitalize">{listener.is_active ? 'Active' : 'Paused'}</span>
                        </div>
                        {listener.listener_type === 'EMAIL_GRAPH' && (
                            <Button 
                                variant={listener.subscription_id ? "outline" : "default"} 
                                size="sm" 
                                onClick={() => handleSubscribeGraph(listener)}
                                className="text-xs h-7"
                            >
                                {listener.subscription_id ? 'Update Webhook' : 'Subscribe'}
                            </Button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );

    useEffect(() => {
        onToolbarChange?.(filterControls);
    }, [searchQuery, viewLayout, showAllTenants, filterTenantId, tenantsList]);

    return (
        <div>

            {loadingListeners && !listeners ? <LoadingSpinner /> : (
                <>
                    {(!listeners || filteredListeners.length === 0) ? (
                        <div className="text-center py-12">
                            <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                <Zap className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:white mb-2">
                                No event listeners found
                            </h3>
                            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
                                Automatically trigger workflows when events occur in external applications like incoming emails.
                            </p>
                            <Button onClick={openCreateModal}>
                                <PlusCircle className="mr-2 h-4 w-4" /> Create Listener
                            </Button>
                        </div>
                    ) : (
                        viewLayout === 'list' ? renderListView(filteredListeners) : renderCardView(filteredListeners)
                    )}
                </>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingListener ? 'Edit Listener' : 'Create New Listener'}
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    {isAdmin && (
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Workspace</label>
                            {editingListener ? (
                                <div className="bg-purple-50 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded p-3 text-sm text-purple-800 dark:text-purple-200">
                                    <span className="font-bold">Workspace:</span> {tenantsMap[editingListener.tenant_id] || editingListener.tenant_id}
                                </div>
                            ) : (
                                <select
                                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 dark:text-white"
                                    value={targetTenantId || ''}
                                    onChange={(e) => setTargetTenantId(e.target.value)}
                                    required
                                >
                                    <option value="" disabled className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white">-- Select Workspace --</option>
                                    {isAdmin && currentUser?.roleId === 'r3' && (
                                        <option value={currentUser.tenantId} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white">🏢 My Workspace</option>
                                    )}
                                    {tenantsList?.filter(t => t.status === 'Active').map(t => (
                                        <option key={t.id} value={t.id} className="bg-white dark:bg-gray-700 text-gray-900 dark:text-white">{t.name}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Listener Type</label>
                        <Select value={formData.listener_type || ''} onValueChange={(v) => handleSelectChange('listener_type', v)} disabled={isAdmin && !targetTenantId} required>
                            <SelectTrigger className="w-full bg-white dark:bg-slate-900">
                                <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                                {LISTENER_TYPES.map(type => (
                                    <SelectItem key={type.value} value={type.value}>
                                        <div className="flex items-center">
                                            {getTypeIcon(type.value)}
                                            <span className="ml-2">{type.label}</span>
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {isAdmin && !targetTenantId && <p className="text-xs text-amber-500 mt-1">Please select a Target Workspace first.</p>}
                    </div>

                    {formData.listener_type && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Workflow</label>
                                <Select value={formData.target_flow_id || ''} onValueChange={(v) => handleSelectChange('target_flow_id', v)} required>
                                    <SelectTrigger className="w-full bg-white dark:bg-slate-900">
                                        <SelectValue placeholder="Select a workflow to trigger" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {flows?.map(f => (
                                            <SelectItem key={f.id} value={f.id}>
                                                {f.publishedName ? `${f.name} (Published as ${f.publishedName})` : f.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="mt-1 text-xs text-gray-500">The workflow to run when an event is received.</p>
                            </div>

                            {formData.listener_type === 'EMAIL_GRAPH' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Integration Connection</label>
                                    <Select value={formData.integration_id || undefined} onValueChange={(v) => handleSelectChange('integration_id', v)}>
                                        <SelectTrigger className="w-full bg-white dark:bg-slate-900">
                                            <SelectValue placeholder="Select Microsoft Graph Integration" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Use Default System Connection</SelectItem>
                                            {apiKeys?.filter(k => k.provider === 'azure_ad' || k.provider === 'ms_graph')?.map(k => (
                                                <SelectItem key={k.id} value={k.id}>{k.name || `OAuth (${k.provider})`}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="mt-1 text-xs text-gray-500">Select the API Key used to access the mailbox.</p>
                                </div>
                            )}
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {formData.listener_type === 'EMAIL_GRAPH' ? 'Mailbox Address' : 'Webhook Source Path'}
                                </label>
                                <Input
                                    name="source_address"
                                    value={formData.source_address}
                                    onChange={handleChange}
                                    placeholder={formData.listener_type === 'EMAIL_GRAPH' ? 'support@yourdomain.com' : 'e.g. /webhook/custom'}
                                    required
                                    className="bg-white dark:bg-slate-900"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    {formData.listener_type === 'EMAIL_GRAPH'
                                        ? "The specific email address this listener monitors."
                                        : "The relative path to trigger this webhook. Must start with /webhook/"}
                                </p>
                            </div>

                            <div className="space-y-4 border-t border-gray-100 dark:border-slate-800 pt-4 mt-4">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Event Filtering Criteria</h3>
                                {formData.listener_type === 'EMAIL_GRAPH' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Subject Contains</label>
                                            <Input
                                                value={criteriaObj?.subject_contains || ''}
                                                onChange={(e) => updateCriteria('subject_contains', e.target.value)}
                                                placeholder="e.g. urgent, invoice"
                                                className="bg-white dark:bg-slate-900 text-xs h-8"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Body Contains</label>
                                            <Input
                                                value={criteriaObj?.body_contains || ''}
                                                onChange={(e) => updateCriteria('body_contains', e.target.value)}
                                                placeholder="e.g. action required"
                                                className="bg-white dark:bg-slate-900 text-xs h-8"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">From Email Contains</label>
                                            <Input
                                                value={criteriaObj?.from_contains || ''}
                                                onChange={(e) => updateCriteria('from_contains', e.target.value)}
                                                placeholder="e.g. @aidasapps.com"
                                                className="bg-white dark:bg-slate-900 text-xs h-8"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Filter Attachments</label>
                                            <div className="flex items-center mt-2">
                                                <input
                                                    type="checkbox"
                                                    id="has_attachments"
                                                    checked={!!criteriaObj?.has_attachments}
                                                    onChange={(e) => updateCriteria('has_attachments', e.target.checked)}
                                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                />
                                                <label htmlFor="has_attachments" className="ml-2 block text-xs text-gray-900 dark:text-gray-300">
                                                    Must Have Attachments
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Advanced Criteria (JSON Overrides)</label>
                                    <FormTextarea
                                        name="criteria_json"
                                        value={typeof formData.criteria_json === 'object' ? JSON.stringify(formData.criteria_json, null, 2) : formData.criteria_json}
                                        onChange={(e) => setFormData({ ...formData, criteria_json: e.target.value })}
                                        placeholder='{"subject_contains": "urgent"}'
                                        rows={3}
                                        className="font-mono text-xs bg-white dark:bg-slate-900"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">Edit the raw JSON to add complex filters like <code className="bg-gray-100 dark:bg-slate-800 p-0.5 rounded">cc_contains</code> or <code className="bg-gray-100 dark:bg-slate-800 p-0.5 rounded">attachment_mime_type</code>.</p>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 dark:border-slate-800 mt-6">
                        <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                        <Button
                            type="submit"
                            disabled={!formData.listener_type || !formData.target_flow_id || !formData.source_address}
                        >
                            {editingListener ? 'Save Changes' : 'Create Listener'}
                        </Button>
                    </div>
                </form>
            </Modal>



            <ConfirmModal
                isOpen={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={handleDelete}
                title="Delete Event Listener"
                message={`Are you sure you want to delete the listener for ${confirmDelete?.source_address}? This will stop automated workflow triggers for this source.`}
                confirmText="Delete"
                confirmColor="error"
            />

            <ConfirmModal
                isOpen={!!confirmSubscribe}
                onClose={() => {
                    setConfirmSubscribe(null);
                    setLocalhostDomain('');
                }}
                onConfirm={executeSubscribeGraph}
                title="Subscribe to Webhooks"
                message={
                    <div>
                        <p className="mb-4 text-gray-700 dark:text-gray-300">Are you sure you want to subscribe to Microsoft Graph webhooks for this listener?</p>
                        {isLocalhost && (
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Localhost Endpoint Domain (ngrok/forwarder URL)</label>
                                <input 
                                    type="text" 
                                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary text-gray-900 dark:text-white"
                                    placeholder="e.g. https://xxxx.ngrok.app"
                                    value={localhostDomain}
                                    onChange={(e) => setLocalhostDomain(e.target.value)}
                                />
                                <p className="text-xs text-amber-500 mt-1">Required for local testing so MS Graph can reach your machine.</p>
                            </div>
                        )}
                    </div>
                }
                confirmText="Subscribe"
                confirmColor="primary"
                isLoading={isSubscribing}
                isConfirmDisabled={isLocalhost && !localhostDomain.trim()}
            />
        </div>
    );
};

export default EventListenersTab;

